/**
 * Per-user, per-browser control for the two session recorders (Sentry Replay +
 * Mixpanel session recording).
 *
 * The preference is persisted in localStorage and read by GeckoAnalyticsProvider
 * at startup to decide whether recording auto-starts. Default is ON (recording
 * enabled); the provider only honors a *disable* for Gecko-internal users, so a
 * customer can never silence their own (or other teams') telemetry. setEnabled()/
 * toggle() also start/stop recording live (no reload needed); note that stopping
 * does not immediately free already-buffered records.
 */
import * as Sentry from '@sentry/react'
import mixpanel from 'mixpanel-browser'

const STORAGE_KEY = 'forecast.sessionRecordingEnabled'

// mixpanel-browser's session-recording controls aren't in its published types.
const mp = mixpanel as unknown as {
  start_session_recording?: () => void
  stop_session_recording?: () => void
}
const sentryReplay = Sentry as unknown as {
  getReplay?: () => { start: () => void; stop: () => void } | undefined
}

function readPreference(): boolean {
  try {
    // Default ON; only an explicit 'false' disables recording.
    return localStorage.getItem(STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

function applyLive(enabled: boolean): void {
  try {
    if (enabled) mp.start_session_recording?.()
    else mp.stop_session_recording?.()
  } catch {
    // mixpanel not initialized yet
  }
  try {
    const replay = sentryReplay.getReplay?.()
    if (replay) {
      if (enabled) replay.start()
      else replay.stop()
    }
  } catch {
    // replay already in the requested state
  }
}

function setEnabled(enabled: boolean): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, String(enabled))
  } catch {
    // ignore storage failures (e.g. private mode)
  }
  applyLive(enabled)
  return enabled
}

export const sessionRecordingControl = {
  isEnabled: readPreference,
  setEnabled,
  toggle: () => setEnabled(!readPreference()),
}
